import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Dialog, DialogContent, DialogTitle, IconButton, Typography, useMediaQuery,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ImportPage } from 'services/imports/fileToPages';

const TITLE_ID = 'imports-source-preview-title';

const useStyles = makeStyles((theme: Theme) => createStyles({
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  titleText: {
    flex: 1,
    minWidth: 0,
  },
  close: {
    minWidth: theme.spacing(5.5),
    minHeight: theme.spacing(5.5),
  },
  icon: {
    fontSize: theme.typography.body1.fontSize,
  },
  content: {
    padding: 0,
    backgroundColor: theme.palette.background.default,
  },
  image: {
    display: 'block',
    width: '100%',
    height: 'auto',
  },
}));

type SourcePreviewDialogProps = {
  page: ImportPage;
  open: boolean;
  onClose: () => void;
};

/** The page image at full dialog width; the dialog content is the only scroll owner. */
export const SourcePreviewDialog = ({ page, open, onClose }: SourcePreviewDialogProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const fullScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const title: string = t('imports:viewer.previewTitle', { file: page.fileName, page: page.pageNumber });
  const closeLabel: string = t('imports:viewer.close');

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="lg" scroll="paper" aria-labelledby={TITLE_ID}>
      <DialogTitle className={classes.title}>
        <Typography id={TITLE_ID} variant="h6" component="h2" noWrap title={title} className={classes.titleText}>
          {title}
        </Typography>
        <IconButton
          aria-label={closeLabel}
          onClick={onClose}
          className={classes.close}
          size="large"
        >
          <FontAwesomeIcon icon={faTimes} className={classes.icon} aria-hidden="true" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.content}>
        <img
          src={page.imageDataUrl}
          alt={t('imports:pages.thumbnailAlt', { file: page.fileName, page: page.pageNumber })}
          className={classes.image}
        />
      </DialogContent>
    </Dialog>
  );
};
